interface EnumObjectType {
    [key: string]: string | number | EnumObjectType;
}

interface EnumOptionType {
    value: number;
    label: string;
}

interface GapsByCategoryType {
    name: string;
    measure_category: MeasureCategoryType;
    gaps: GapType[];
}

interface StatusRollupsType {
    [gapType: number]: {
        [status: number]: number[];
    };
}

export function enums(item: string | (string | number)[]) {
    if (!Array.isArray(item)) return window.app.enums[item];
    return item.reduce((val, cur) => val[cur], window.app.enums);
}

export function enumValue(item: string | string[]): number | false {
    let final: string | undefined;
    if (!Array.isArray(item)) {
        final = Object.keys(window.app.enums).find((x) => window.app.enums[x] === item);
    } else {
        const itemCopy = [...item];
        const lastVal = itemCopy.pop();
        const mapped = itemCopy.reduce((val, cur) => val[cur], window.app.enums) as EnumObjectType;
        final = Object.keys(mapped).find((x) => mapped[x] === lastVal);
    }
    if (typeof final === 'undefined') {
        return false;
    }
    return parseInt(final);
}

export function enumsToOptions(item: string | string[]): EnumOptionType[] {
    const itemList = enums(item);
    return Object.keys(itemList).map((x) => ({ value: parseInt(x), label: itemList[x] }));
}

function statusRollups(): StatusRollupsType {
    return {
        [enumValue(['gap', 'type', 'FOC Gap']) as number]: {
            [enumValue(['gap', 'status', 'Addressed']) as number]: [
                enumValue(['gap', 'status', 'Addressed']) as number,
                enumValue(['gap', 'status', 'Confirmed']) as number,
                enumValue(['gap', 'status', 'Disagree/Resolved/Closed']) as number,
                enumValue(['gap', 'status', 'Non-DataRap Covered']) as number,
            ],
        },
        [enumValue(['gap', 'type', 'Quality Gap']) as number]: {
            [enumValue(['gap', 'status', 'Pending']) as number]: [
                enumValue(['gap', 'status', 'Pending']) as number,
                enumValue(['gap', 'status', 'New']) as number,
                enumValue(['gap', 'status', '']) as number,
                enumValue(['gap', 'status', 'Fail']) as number,
                enumValue(['gap', 'status', 'Failed Validation']) as number,
            ],
            [enumValue(['gap', 'status', 'Addressed']) as number]: [
                enumValue(['gap', 'status', 'Addressed']) as number,
                enumValue(['gap', 'status', 'Confirmed']) as number,
            ],
            [enumValue(['gap', 'status', 'Fail']) as number]: [
                enumValue(['gap', 'status', 'Fail']) as number,
                enumValue(['gap', 'status', 'Failed Validation']) as number,
            ],
        },
        [enumValue(['gap', 'type', 'Risk Gap']) as number]: {
            [enumValue(['gap', 'status', 'Pending']) as number]: [
                enumValue(['gap', 'status', 'Pending']) as number,
                enumValue(['gap', 'status', 'Fail']) as number,
                enumValue(['gap', 'status', 'Failed Validation']) as number,
                enumValue(['gap', 'status', 'Insufficient']) as number,
            ],
            [enumValue(['gap', 'status', 'Addressed']) as number]: [
                enumValue(['gap', 'status', 'Addressed']) as number,
                enumValue(['gap', 'status', 'Confirmed']) as number,
                enumValue(['gap', 'status', 'Disagree/Resolved/Closed']) as number,
                enumValue(['gap', 'status', 'Non-DataRap Covered']) as number,
            ],
            [enumValue(['gap', 'status', 'Fail']) as number]: [
                enumValue(['gap', 'status', 'Fail']) as number,
                enumValue(['gap', 'status', 'Insufficient']) as number,
            ],
        },
    };
}

export function getGapStatusRollups(
    gapType: number | null,
    status: string | number
): (string | number)[] {
    if (gapType === null) return [status];
    return statusRollups()[gapType]?.[Number(status)] || [status];
}

export function isInRolledUpStatus(
    gapType: number | null,
    parentStatus: number,
    childStatus: number
): boolean {
    return getGapStatusRollups(gapType, parentStatus).indexOf(childStatus) > -1;
}

export function getGapTypeBySection(section: string): number | null {
    switch (section) {
        case 'foc_gaps':
            return enumValue(['gap', 'type', 'FOC Gap']) as number;
        case 'quality_gaps':
            return enumValue(['gap', 'type', 'Quality Gap']) as number;
        case 'risk_gaps':
            return enumValue(['gap', 'type', 'Risk Gap']) as number;
        default:
            return null;
    }
}

export function getRollupStatusForGapType(gapType: string, status: number): number {
    const sectionRollup = statusRollups()[getGapTypeBySection(gapType) as number] || {};
    return parseInt(
        Object.keys(sectionRollup).find((key) =>
            sectionRollup[parseInt(key)].includes(status)
        ) as string
    );
}

export function gapIsAddressed(gap: GapType | RiskGapType): boolean {
    return isInRolledUpStatus(
        gap.type,
        enumValue(['gap', 'status', 'Addressed']) as number,
        gap.status
    );
}

export function gapIsFailed(gap: GapType | RiskGapType): boolean {
    return isInRolledUpStatus(gap.type, enumValue(['gap', 'status', 'Fail']) as number, gap.status);
}

export function gapIsPending(gap: GapType | RiskGapType): boolean {
    return isInRolledUpStatus(
        gap.type,
        enumValue(['gap', 'status', 'Pending']) as number,
        gap.status
    );
}

export function gapIsUnderReview(gap: GapType | RiskGapType): boolean {
    return isInRolledUpStatus(
        gap.type,
        enumValue(['gap', 'status', 'Under Review']) as number,
        gap.status
    );
}

export function getGapsByCategory(gaps: GapType[], status?: number): GapsByCategoryType[] {
    const gapsByCategory: GapsByCategoryType[] = [];
    const categories: { [key: string]: number } = {};

    gaps.forEach((gap) => {
        let showGap = !status || isInRolledUpStatus(gap.type, status, gap.status);
        // Quality gaps have conditions where non-pending cards show along Pending
        if (!showGap && gap.type === (enumValue(['gap', 'type', 'Quality Gap']) as number)) {
            if (
                status === (enumValue(['gap', 'status', 'Pending']) as number) &&
                gapIsAddressed(gap) &&
                gap.measure?.is_manual_add_compatible
            ) {
                showGap =
                    gap.submission_answer?.status ===
                    (enumValue(['submission', 'status', 'New']) as number);
            }
            if (
                status === (enumValue(['gap', 'status', 'Addressed']) as number) &&
                isInRolledUpStatus(
                    gap.type,
                    enumValue(['gap', 'status', 'Addressed']) as number,
                    gap.system_status as number
                ) &&
                gapIsUnderReview(gap)
            ) {
                showGap = true;
            }
        }
        if (showGap) {
            const measureCategoryName =
                gap.measure?.measure_category?.description || 'No Measure Category';
            // Handle gap categories we haven't seen
            if (typeof categories[measureCategoryName] === 'undefined') {
                categories[measureCategoryName] = gapsByCategory.length;
                gapsByCategory.push({
                    name: measureCategoryName,
                    measure_category: gap.measure?.measure_category as MeasureCategoryType,
                    gaps: [gap],
                });
            } else {
                gapsByCategory[categories[measureCategoryName]].gaps.push(gap);
            }
        }
    });

    // Sort categories
    gapsByCategory.sort((cat1, cat2) => {
        return cat1.measure_category.sequence - cat2.measure_category.sequence;
    });

    // Sort gaps
    gapsByCategory.forEach((cat) => {
        cat.gaps.sort((gap1, gap2) => {
            return (gap1.measure?.sequence || 0) - (gap2.measure?.sequence || 0);
        });
    });

    return gapsByCategory;
}

export function gapShouldShowControl(selectedStatus: number, gapType?: number): boolean {
    const selectedStatuses = [
        enumValue(['gap', 'status', 'Pending']) as number,
        enumValue(['gap', 'status', 'Fail']) as number,
    ];

    if (typeof gapType !== 'undefined') {
        switch (gapType) {
            case enumValue(['gap', 'type', 'Quality Gap']) as number:
                selectedStatuses.push(enumValue(['gap', 'status', 'Addressed']) as number);
                break;
            default:
                break;
        }
    }

    /* Only show Add Condition button, Date of Visit picker, Submit button,
     and Attachments button when viewing Pending or Failed Risk Gaps */
    return selectedStatuses.includes(selectedStatus);
}
